/**
 * Admin Analytics Service
 * Provides cross-tenant analytics and reporting for the SaaS platform
 */

import {
  collection,
  query,
  getDocs,
} from 'firebase/firestore';
import { db } from 'foodhub-shared/firebase';

const ORDERS_COLLECTION = 'orders';
const SUBSCRIPTIONS_COLLECTION = 'subscriptions';

/**
 * Get cross-tenant revenue analytics
 */
export const getCrossTenantRevenue = async () => {
  try {
    const q = query(collection(db, ORDERS_COLLECTION));
    const querySnapshot = await getDocs(q);

    const revenueByTenant: Record<string, number> = {};
    let totalRevenue = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const tenantId = data.tenantId || 'unknown';
      const amount = data.total || 0;

      revenueByTenant[tenantId] = (revenueByTenant[tenantId] || 0) + amount;
      totalRevenue += amount;
    });

    return {
      totalRevenue,
      revenueByTenant,
      topTenants: Object.entries(revenueByTenant)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([tenantId, revenue]) => ({ tenantId, revenue })),
    };
  } catch (error: any) {
    console.error('Get cross-tenant revenue error:', error);
    return {
      totalRevenue: 0,
      revenueByTenant: {},
      topTenants: [],
    };
  }
};

/**
 * Get cross-tenant order analytics
 */
export const getCrossTenantOrders = async () => {
  try {
    const q = query(collection(db, ORDERS_COLLECTION));
    const querySnapshot = await getDocs(q);

    const ordersByTenant: Record<string, number> = {};
    const ordersByStatus: Record<string, number> = {};
    let totalOrders = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const tenantId = data.tenantId || 'unknown';
      const status = data.status || 'unknown';

      ordersByTenant[tenantId] = (ordersByTenant[tenantId] || 0) + 1;
      ordersByStatus[status] = (ordersByStatus[status] || 0) + 1;
      totalOrders += 1;
    });

    return {
      totalOrders,
      ordersByTenant,
      ordersByStatus,
      topTenants: Object.entries(ordersByTenant)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([tenantId, count]) => ({ tenantId, count })),
    };
  } catch (error: any) {
    console.error('Get cross-tenant orders error:', error);
    return {
      totalOrders: 0,
      ordersByTenant: {},
      ordersByStatus: {},
      topTenants: [],
    };
  }
};

/**
 * Get subscription analytics
 */
export const getSubscriptionAnalytics = async () => {
  try {
    const q = query(collection(db, SUBSCRIPTIONS_COLLECTION));
    const querySnapshot = await getDocs(q);

    const subscriptionsByStatus: Record<string, number> = {};
    const subscriptionsByPlan: Record<string, number> = {};
    let totalSubscriptions = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const status = data.status || 'unknown';
      const plan = data.plan || 'unknown';

      subscriptionsByStatus[status] = (subscriptionsByStatus[status] || 0) + 1;
      subscriptionsByPlan[plan] = (subscriptionsByPlan[plan] || 0) + 1;
      totalSubscriptions += 1;
    });

    return {
      totalSubscriptions,
      subscriptionsByStatus,
      subscriptionsByPlan,
      activeSubscriptions: subscriptionsByStatus['active'] || 0,
      trialSubscriptions: subscriptionsByStatus['trial'] || 0,
      cancelledSubscriptions: subscriptionsByStatus['cancelled'] || 0,
    };
  } catch (error: any) {
    console.error('Get subscription analytics error:', error);
    return {
      totalSubscriptions: 0,
      subscriptionsByStatus: {},
      subscriptionsByPlan: {},
      activeSubscriptions: 0,
      trialSubscriptions: 0,
      cancelledSubscriptions: 0,
    };
  }
};

/**
 * Get platform health metrics
 */
export const getPlatformHealth = async () => {
  try {
    const [revenue, orders, subscriptions] = await Promise.all([
      getCrossTenantRevenue(),
      getCrossTenantOrders(),
      getSubscriptionAnalytics(),
    ]);

    return {
      totalRevenue: revenue.totalRevenue,
      totalOrders: orders.totalOrders,
      totalSubscriptions: subscriptions.totalSubscriptions,
      activeSubscriptions: subscriptions.activeSubscriptions,
      averageOrderValue: orders.totalOrders > 0 ? revenue.totalRevenue / orders.totalOrders : 0,
      topTenants: revenue.topTenants,
    };
  } catch (error: any) {
    console.error('Get platform health error:', error);
    return {
      totalRevenue: 0,
      totalOrders: 0,
      totalSubscriptions: 0,
      activeSubscriptions: 0,
      averageOrderValue: 0,
      topTenants: [],
    };
  }
};

export default {
  getCrossTenantRevenue,
  getCrossTenantOrders,
  getSubscriptionAnalytics,
  getPlatformHealth,
};

